import axios from 'axios'
import { getLocale } from '@/i18n'

// Single source of truth for the backend base URL.
// In dev the backend runs on the same host as the frontend, on port 3001.
// For production, set VUE_APP_API_BASE at build time (e.g. https://api.trm.uz).
// In dev (VUE_APP_API_BASE undefined) → talk to the backend on :3001.
// In production we set VUE_APP_API_BASE="" so the app uses SAME-ORIGIN calls
// (e.g. /news, /api/v1, /uploads), which nginx routes to the backend. Using `??`
// (not `||`) lets an explicit empty string mean "same origin".
export const API_BASE =
  process.env.VUE_APP_API_BASE ?? `http://${window.location.hostname}:3001`

export const http = axios.create({ baseURL: API_BASE })

// Tag every request with the active UI locale so the backend returns DB content
// (news, documents, people, …) in the selected language.
http.interceptors.request.use((config) => {
  config.params = { ...(config.params || {}), lang: getLocale() }
  return config
})

// Resolve a media path returned by the backend. Absolute URLs pass through;
// root-relative paths (e.g. /uploads/x.png) are prefixed with the API origin so
// they load from the backend regardless of which host serves the frontend.
// A bare value (a bundled asset filename) is returned unchanged for the caller
// to resolve against its local assets.
export function assetUrl(path) {
  if (!path) return ''
  if (/^https?:\/\//.test(path)) return path
  if (path.startsWith('/')) return `${API_BASE}${path}`
  return path
}

// Resolve a content image to a usable src. Admin-uploaded paths (absolute or
// /uploads/…) go through assetUrl; a bare filename is resolved against the
// bundled seed images. `fallback` is returned when the name is empty or the
// bundled file doesn't exist (e.g. a logo placeholder for people photos).
export function resolveImage(name, fallback = '') {
  if (!name) return fallback
  if (/^https?:\/\//.test(name) || name.startsWith('/')) return assetUrl(name)
  try {
    return require(`@/assets/img/images/${name}`)
  } catch {
    return fallback
  }
}

// ── YouTube helpers ───────────────────────────────────────────────────────────
// A video's `url` may be either an uploaded file path (/uploads/…) or a pasted
// YouTube link. These extract the 11-char id so we can embed it / show its
// thumbnail. Returns '' for anything that isn't a recognisable YouTube URL.
export function youtubeId(url) {
  if (!url || typeof url !== 'string') return ''
  const m = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/)
  return m ? m[1] : ''
}

export function youtubeEmbed(url) {
  const id = youtubeId(url)
  return id ? `https://www.youtube.com/embed/${id}` : ''
}

export function youtubeThumb(url) {
  const id = youtubeId(url)
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : ''
}

export default http
